"use client";

import CdnImage from "@/components/CdnImage";
import GradientAvatar from "@/app/components/GradientAvatar";
import DextoolsLandingBar from "@/app/components/DextoolsLandingBar";
import Link from "next/link";
import dynamic from "next/dynamic";
import { createLogger } from "@/lib/logger";
import { FiArrowLeft, FiCopy, FiCheck, FiTrendingUp, FiDollarSign, FiCalendar, FiShield, FiUsers } from "react-icons/fi";
import { useState, useEffect, Children } from "react";

const logger = createLogger("UserProfile");
import { Eye, Trophy, Target, Star, Medal, Diamond, Users, TrendingUp } from "lucide-react";
import PredexonStatsPanel from "./PredexonStatsPanel";
import { useEnableTrading } from "@/hooks/useEnableTrading";
import { useT } from "@/hooks/useT";

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path fillRule="evenodd" clipRule="evenodd" d="M11.61 29.9204C11.1891 29.7427 10.9421 29.3022 11.01 28.8504L12.83 17.0004H8.00005C7.6897 17.0087 7.39308 16.8724 7.19723 16.6316C7.00139 16.3907 6.92853 16.0725 7.00005 15.7704L10 2.77036C10.1089 2.30919 10.5264 1.9877 11 2.00036H21C21.3026 1.99933 21.5894 2.13537 21.78 2.37036C21.9734 2.60808 22.0471 2.92135 21.98 3.22036L20.25 11.0004H25C25.3824 10.9996 25.7318 11.217 25.9 11.5604C26.0459 11.8892 26.0039 12.2711 25.79 12.5604L12.79 29.5604C12.6119 29.8245 12.3184 29.9879 12 30.0004C11.8662 29.9979 11.734 29.9707 11.61 29.9204ZM17.75 13.0004L19.75 4.00036H11.8L9.26005 15.0004H15.17L13.58 25.2804L23 13.0004H17.75Z" fill="currentColor" />
    </svg>
  );
}

function VolumeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path fillRule="evenodd" clipRule="evenodd" d="M17.5001 8.5L22.4395 13.4393L17.0001 18.8787L11.0001 12.8787L0.439453 23.4393L2.56077 25.5607L11.0001 17.1213L17.0001 23.1213L24.5608 15.5607L29.5001 20.5L32.0001 18V6H20.0001L17.5001 8.5ZM29.0001 15.7574L22.2428 9H29.0001V15.7574Z" fill="currentColor" />
    </svg>
  );
}

function PnlIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M16 2.66602C16.6836 2.66618 17.2471 3.18081 17.3242 3.84375L17.334 3.99902V6.66602L18.7246 6.66699C20.9003 6.7611 22.8005 8.16875 23.5244 10.2227C23.7692 10.9172 23.4045 11.6791 22.71 11.9238C22.0651 12.1511 21.3624 11.8527 21.0693 11.2529L21.0088 11.1094C20.6709 10.1508 19.8207 9.47377 18.8711 9.35059L18.667 9.33301H13.333C11.8605 9.33318 10.6672 10.5265 10.667 11.999C10.667 13.4048 11.755 14.5574 13.1348 14.6592L13.333 14.666H18.667C21.6123 14.6662 23.9998 17.0538 24 19.999C24 22.8553 21.7545 25.1875 18.9326 25.3262L18.667 25.333L17.334 25.332V27.999C17.334 28.7353 16.7362 29.3328 16 29.333C15.3165 29.3328 14.753 28.8181 14.6758 28.1553L14.667 27.999V25.3311H13.2754C11.0998 25.2369 9.19958 23.8301 8.47559 21.7764C8.23082 21.0819 8.59559 20.32 9.29004 20.0752C9.93494 19.8479 10.6376 20.1463 10.9307 20.7461L10.9912 20.8896C11.3291 21.8481 12.1794 22.5253 13.1289 22.6484L13.333 22.666H18.667C20.1396 22.6658 21.333 21.4717 21.333 19.999C21.3328 18.5934 20.2449 17.4416 18.8652 17.3398L18.667 17.333H13.333C10.3876 17.3328 8 14.9444 8 11.999C8.00017 9.14291 10.2456 6.81155 13.0674 6.67285L13.333 6.66602H14.667V3.99902C14.6672 3.2629 15.2639 2.66619 16 2.66602Z" fill="currentColor" />
    </svg>
  );
}

function PositionsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M19.3334 11.3333L18.957 12.7853C19.2374 12.858 19.5329 12.848 19.8078 12.7564L19.3334 11.3333ZM9.25107 24.8347L10.6865 25.27C10.8849 24.6156 10.6157 23.9099 10.0319 23.554L9.25107 24.8347ZM26.6703 22.1803C27.046 21.442 26.7521 20.5389 26.0138 20.1632C25.2754 19.7874 24.3723 20.0813 23.9966 20.8197L25.3334 21.5L26.6703 22.1803ZM22.1215 24.8347L21.3407 23.554C20.7569 23.9099 20.4877 24.6156 20.6861 25.27L22.1215 24.8347ZM8.60432 26.9679L7.16885 26.5326L7.16885 26.5327L8.60432 26.9679ZM12.2022 27.8399L11.3019 26.6401L11.3019 26.6401L12.2022 27.8399ZM14.0617 26.4444V24.9444C13.737 24.9444 13.421 25.0498 13.1613 25.2447L14.0617 26.4444ZM22.7683 26.9679L24.2038 26.5327L24.2037 26.5326L22.7683 26.9679ZM19.1704 27.8399L20.0707 26.6401L20.0707 26.6401L19.1704 27.8399ZM17.3109 26.4444L18.2113 25.2447C17.9516 25.0498 17.6356 24.9444 17.3109 24.9444V26.4444ZM25.3334 9.33333L26.833 9.36805C26.8443 8.87989 26.6173 8.41678 26.2245 8.1267C25.8317 7.83663 25.3223 7.7559 24.8591 7.91031L25.3334 9.33333ZM25.2214 14.1711L23.7218 14.1364C23.7075 14.7536 24.073 15.3166 24.6426 15.5549L25.2214 14.1711ZM28.0001 15.3333H29.5001C29.5001 14.7285 29.1369 14.1829 28.5789 13.9495L28.0001 15.3333ZM28.0001 20L28.6913 21.3312C29.1882 21.0732 29.5001 20.5599 29.5001 20H28.0001ZM24.741 20.0021C24.0058 20.3838 23.7192 21.2893 24.101 22.0246C24.4827 22.7598 25.3882 23.0463 26.1234 22.6646L25.4322 21.3333L24.741 20.0021ZM4.16675 13.3333C4.16675 12.5049 3.49518 11.8333 2.66675 11.8333C1.83832 11.8333 1.16675 12.5049 1.16675 13.3333H2.66675H4.16675ZM4.90239 18.7701C5.69588 19.0081 6.53211 18.5578 6.77015 17.7644C7.0082 16.9709 6.55793 16.1346 5.76444 15.8966L5.33341 17.3333L4.90239 18.7701ZM5.33341 18.6111H6.83341C6.83341 16.9806 7.69091 15.4021 9.27887 14.1835C10.8684 12.9638 13.1287 12.1667 15.6863 12.1667V10.6667V9.16667C12.5261 9.16667 9.61005 10.1479 7.45253 11.8035C5.29349 13.4603 3.83341 15.854 3.83341 18.6111H5.33341ZM15.6863 10.6667V12.1667C16.6091 12.1667 17.8373 12.495 18.957 12.7853L19.3334 11.3333L19.7098 9.88133C18.7477 9.63191 17.0827 9.16667 15.6863 9.16667V10.6667ZM9.25107 24.8347L10.0319 23.554C7.96792 22.2956 6.83341 20.4793 6.83341 18.6111H5.33341H3.83341C3.83341 21.7856 5.76009 24.4632 8.47023 26.1155L9.25107 24.8347ZM25.3334 21.5L23.9966 20.8197C23.4702 21.854 22.5642 22.8081 21.3407 23.554L22.1215 24.8347L22.9024 26.1155C24.5232 25.1273 25.8534 23.7853 26.6703 22.1803L25.3334 21.5ZM9.25107 24.8347L7.8156 24.3995L7.16885 26.5326L8.60432 26.9679L10.0398 27.4031L10.6865 25.27L9.25107 24.8347ZM9.3699 28V29.5H11.722V28V26.5H9.3699V28ZM12.2022 27.8399L13.1026 29.0396L14.962 27.6442L14.0617 26.4444L13.1613 25.2447L11.3019 26.6401L12.2022 27.8399ZM22.1215 24.8347L20.6861 25.27L21.3328 27.4031L22.7683 26.9679L24.2037 26.5326L23.557 24.3995L22.1215 24.8347ZM22.0027 28V26.5H19.6506V28V29.5H22.0027V28ZM19.1704 27.8399L20.0707 26.6401L18.2113 25.2447L17.3109 26.4444L16.4106 27.6442L18.27 29.0396L19.1704 27.8399ZM14.0617 26.4444V27.9444H17.3109V26.4444V24.9444H14.0617V26.4444ZM19.6506 28V26.5C19.8021 26.5 19.9495 26.5492 20.0707 26.6401L19.1704 27.8399L18.27 29.0396C18.6682 29.3384 19.1527 29.5 19.6506 29.5V28ZM22.7683 26.9679L21.3328 27.4031C21.1965 26.9536 21.5329 26.5 22.0027 26.5V28V29.5C23.5462 29.5 24.6516 28.0097 24.2038 26.5327L22.7683 26.9679ZM11.722 28V29.5C12.2199 29.5 12.7044 29.3384 13.1026 29.0396L12.2022 27.8399L11.3019 26.6401C11.4231 26.5492 11.5705 26.5 11.722 26.5V28ZM8.60432 26.9679L7.16885 26.5327C6.721 28.0097 7.8264 29.5 9.3699 29.5V28V26.5C9.83966 26.5 10.1761 26.9536 10.0398 27.4031L8.60432 26.9679ZM19.3334 11.3333L19.8078 12.7564L25.8078 10.7564L25.3334 9.33333L24.8591 7.91031L18.8591 9.91031L19.3334 11.3333ZM25.3334 9.33333L23.8338 9.29862L23.7218 14.1364L25.2214 14.1711L26.721 14.2058L26.833 9.36805L25.3334 9.33333ZM25.2214 14.1711L24.6426 15.5549L27.4213 16.7172L28.0001 15.3333L28.5789 13.9495L25.8003 12.7872L25.2214 14.1711ZM28.0001 15.3333H26.5001V20H28.0001H29.5001V15.3333H28.0001ZM28.0001 20L27.3089 18.6688L24.741 20.0021L25.4322 21.3333L26.1234 22.6646L28.6913 21.3312L28.0001 20ZM20.6667 17.3333V15.8333C21.127 15.8333 21.5001 16.2064 21.5001 16.6667H20.0001H18.5001C18.5001 17.8633 19.4701 18.8333 20.6667 18.8333V17.3333ZM20.0001 16.6667H21.5001C21.5001 17.1269 21.127 17.5 20.6667 17.5V16V14.5C19.4701 14.5 18.5001 15.4701 18.5001 16.6667H20.0001ZM20.6667 16V17.5C20.2065 17.5 19.8334 17.1269 19.8334 16.6667H21.3334H22.8334C22.8334 15.4701 21.8634 14.5 20.6667 14.5V16ZM21.3334 16.6667H19.8334C19.8334 16.2064 20.2065 15.8333 20.6667 15.8333V17.3333V18.8333C21.8634 18.8333 22.8334 17.8633 22.8334 16.6667H21.3334ZM2.66675 13.3333C1.16675 13.3333 1.16675 13.334 1.16675 13.3346C1.16675 13.3348 1.16675 13.3355 1.16675 13.3359C1.16675 13.3368 1.16675 13.3378 1.16676 13.3388C1.16676 13.3409 1.16677 13.3431 1.16679 13.3456C1.16682 13.3504 1.16688 13.3561 1.16696 13.3626C1.16714 13.3756 1.16744 13.3917 1.16797 13.4108C1.16902 13.4489 1.17096 13.4991 1.17451 13.56C1.18161 13.6813 1.19525 13.8467 1.22157 14.0441C1.27364 14.4346 1.37831 14.9734 1.59346 15.5543C2.02399 16.7167 2.96229 18.188 4.90239 18.7701L5.33341 17.3333L5.76444 15.8966C5.03787 15.6786 4.64284 15.1499 4.4067 14.5124C4.28851 14.1932 4.22653 13.8821 4.19526 13.6476C4.17991 13.5325 4.17272 13.4416 4.1694 13.3848C4.16775 13.3566 4.16708 13.3373 4.16683 13.3282C4.16671 13.3236 4.16668 13.3217 4.1667 13.3225C4.1667 13.3229 4.16671 13.324 4.16673 13.3258C4.16673 13.3267 4.16674 13.3277 4.16674 13.329C4.16674 13.3296 4.16675 13.3303 4.16675 13.331C4.16675 13.3314 4.16675 13.332 4.16675 13.3321C4.16675 13.3327 4.16675 13.3333 2.66675 13.3333ZM8.00008 8.75296H9.50008C9.50008 6.93037 10.9436 5.5 12.6667 5.5V4V2.5C9.2352 2.5 6.50008 5.32558 6.50008 8.75296H8.00008ZM12.6667 4V5.5C14.3899 5.5 15.8334 6.93037 15.8334 8.75296H17.3334H18.8334C18.8334 5.32558 16.0983 2.5 12.6667 2.5V4ZM17.3334 8.75296H15.8334C15.8334 9.13911 15.7685 9.50624 15.6508 9.84551L17.0679 10.3373L18.485 10.8291C18.7112 10.1772 18.8334 9.47786 18.8334 8.75296H17.3334ZM9.25881 12L10.3633 10.9851C9.82928 10.4039 9.50008 9.62145 9.50008 8.75296H8.00008H6.50008C6.50008 10.3956 7.12685 11.8968 8.15429 13.0149L9.25881 12Z" fill="currentColor" />
    </svg>
  );
}

import { toast } from "sonner";
import { EyeIcon } from "@/app/components/icons/EyeIcon";
import { EyeOffIcon } from "@/app/components/icons/EyeOffIcon";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";
import { triggerConfetti } from "@/lib/confetti";
import TraderLabelEditor from "@/app/components/TraderLabelEditor";
import AdminCopiesButton from "./AdminCopiesButton";
import WalletPnLEnriched from "./WalletPnLEnriched";
import WalletVolumeChart from "./WalletVolumeChart";
import MarketPerformance from "./MarketPerformance";

// Lazy-load CopyTraderModal - only needed when user clicks copy button
const CopyTraderModal = dynamic(() => import("./CopyTraderModal"), {
  ssr: false,
});

type UsernameHistoryEntry = {
  old_username: string;
  new_username: string | null;
  changed_at: string;
};

type UserData = {
  username: string;
  pseudonym?: string;
  wallet: string;
  avatar: string;
  bio: string;
  winRate: number;
  totalVolume: string;
  totalPnL?: number;
  profitPercent?: number;
  largestWin?: number;
  profileViews?: number;
  currentBalance?: number;
  copiers: number;
  totalTrades: number;
  verified: boolean;
  joinedDate: string;
  markets: string[];
  positionsAboveEntry?: number;
  recentTrades: Array<{
    id: string;
    market: string;
    position: string;
    outcome: string;
    profit: string;
    date: string;
    type?: "BUY" | "SELL";
    icon?: string;
  }>;
  // Username history
  previousUsername?: string | null;
  usernameChangedAt?: string | null;
  usernameHistory?: UsernameHistoryEntry[];
  // X/Twitter
  xUsername?: string | null;
  // FrenFlow user enrichment
  isFrenFlowUser?: boolean;
  frenFlowUsername?: string | null;
  frenFlowName?: string | null;
  frenFlowAvatar?: string | null;
  // Predexon analytics
  tradingStyle?: string | null;
  secondaryStyles?: string[];
  avgTradeSize?: number;
  realizedPnl?: number | null;
  unrealizedPnl?: number | null;
};

// Check if username was changed recently (within 30 days)
function wasUsernameChangedRecently(changedAt: string | null | undefined): boolean {
  if (!changedAt) return false;
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  return new Date(changedAt) > thirtyDaysAgo;
}

// Format relative time for username change
function formatUsernameChangeTime(changedAt: string): string {
  const date = new Date(changedAt);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "today";
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatCurrency(value: number | null | undefined): string {
  if (value == null || typeof value !== 'number' || isNaN(value)) return "$0.00";
  if (Math.abs(value) >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  } else if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toFixed(2)}K`;
  }
  return `$${value.toFixed(2)}`;
}

function formatCompactCurrency(value: number | null | undefined): string {
  if (value == null || typeof value !== 'number' || isNaN(value)) return "$0";
  if (Math.abs(value) >= 1000000) {
    return `${value / 1000000 >= 0 ? '+' : ''}$${Math.abs(value / 1000000).toFixed(1)}M`;
  } else if (Math.abs(value) >= 1000) {
    return `${value >= 0 ? '+' : '-'}$${Math.abs(value / 1000).toFixed(1)}K`;
  }
  return `${value >= 0 ? '+' : ''}$${value.toFixed(0)}`;
}

function getBalanceBadge(balance: number | undefined): { emoji: string; label: string; color: string } | null {
  if (balance === undefined || balance === null) return null;
  if (balance >= 100000) return { emoji: "🐋", label: "Whale", color: "from-white/20 to-white/5" };
  if (balance >= 50000) return { emoji: "🦈", label: "Shark", color: "from-white/15 to-white/5" };
  if (balance >= 10000) return { emoji: "🐬", label: "Dolphin", color: "from-white/10 to-white/5" };
  if (balance >= 1000) return { emoji: "🐟", label: "Fish", color: "from-white/10 to-transparent" };
  if (balance > 0) return { emoji: "🦐", label: "Shrimp", color: "from-white/5 to-transparent" };
  return null;
}

export default function UserProfile({ user, children, source = 'polymarket', predexonProfile, predexonLoading = false }: { user: UserData; children?: React.ReactNode; source?: string; predexonProfile?: any; predexonLoading?: boolean }) {
  const t = useT();
  const isKalshi = source === 'kalshi';
  const isPredict = source === 'predict';
  const kalshiData = (user as any);

  const { isEnabled: tradingEnabled, enableTrading, isLoading: enablingTrading } = useEnableTrading();

  const handleCopyClick = () => {
    if (isPredict || isKalshi) {
      toast.custom(
        (toastInst) => (
          <div className="flex items-start gap-3 p-4 bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-lg max-w-sm">
            {/* Plain <img> (served from local /public) instead of CdnImage:
                k-icon.svg is not synced to the R2 CDN, so CdnImage's rewrite
                to images.frenflow.com 404s. Matches the ~24 other plain-<img>
                usages of this icon across the app. */}
            <img src={isKalshi ? "/logos/kalshi/k-icon.svg" : "/logos/predictfun/Predict-Icon-BP.png"} alt="" width={20} height={20} className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-inter-tight text-sm font-medium text-white">{t("traders.profile.toast.comingSoon")}</p>
              <p className="font-satoshi text-xs text-gray-400 mt-1">{t("traders.profile.toast.comingSoonDesc", { platform: isKalshi ? 'Kalshi' : 'Predict.fun' })}</p>
            </div>
            <button onClick={() => toast.dismiss(toastInst)} className="text-gray-500 hover:text-white transition-colors">&times;</button>
          </div>
        ),
        { duration: 4000, position: "bottom-right" }
      );
      return;
    }
    // Open copy modal directly — it handles enable trading flow internally
    setIsCopyModalOpen(true);
  };
  const [copied, setCopied] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  // false until the /api/watchlist fetch settles. Gates the favorite star so
  // it doesn't flash empty→filled on entry/refresh for an already-favorited trader.
  const [watchlistResolved, setWatchlistResolved] = useState(false);
  const [watchlistHovered, setWatchlistHovered] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(() => {
    if (typeof window !== 'undefined') return new URLSearchParams(window.location.search).get('copy') === '1';
    return false;
  });
  const [isCopyingTrader, setIsCopyingTrader] = useState(false);
  // false until the /api/copytrading/following fetch settles. Gates the Copy
  // CTA so it doesn't flash "Copy"→"Copying" on entry/refresh for a trader we
  // already copy.
  const [copyStatusResolved, setCopyStatusResolved] = useState(false);
  const [isStoppingCopy, setIsStoppingCopy] = useState(false);
  const [copyLeaderId, setCopyLeaderId] = useState<string | null>(null);
  const [showUsernameHistory, setShowUsernameHistory] = useState(false);
  // HFT-bot badge sourced from the same Predexon all-time signal as the
  // /traders grid (totalTrades / hoursActive). SSR popula los campos en
  // page.tsx:493-494 desde /api/portfolio/traders/:identifier, así que la
  // grid y el perfil siempre coinciden. No refetch post-mount — antes
  // golpeábamos /leader-activity-rate (ventana 24 h) y eso volvía el
  // badge inconsistente para bots con actividad histórica pero quietos
  // las últimas 24 h (caso @0x8dxd, mayo 2026).
  const isHft = (user as any).isHft === true;
  const hftTradesPerHour: number | null =
    typeof (user as any).tradesPerHour === 'number' ? (user as any).tradesPerHour : null;
  const { isLoggedIn, currentWalletAddress } = useAuth();
  // Hide Copy + Copying controls when the trader page is the user's own
  // wallet — copying yourself trips the engine and isn't useful. Match on
  // the trader's primary wallet, which is what the engine subscribes to.
  const isSelfTrader = !!currentWalletAddress && !!user.wallet
    && currentWalletAddress.toLowerCase() === user.wallet.toLowerCase();

  // The viewer's PRIVATE label (tag) for this trader — user_trader_labels,
  // works for any trader (followed or not) and survives unfollow. Rendered
  // as a chip next to the H1; the real handle stays visible (and is what
  // search engines index — the chip is client-fetched post-auth only).
  const [myTraderLabel, setMyTraderLabel] = useState<string | null>(null);
  useEffect(() => {
    if (!isLoggedIn || !user.wallet || !/^0x[a-fA-F0-9]{40}$/.test(user.wallet)) {
      setMyTraderLabel(null);
      return;
    }
    authFetch(`/api/copytrading/trader-label?address=${user.wallet.toLowerCase()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setMyTraderLabel(d.data?.label ?? null); })
      .catch(() => {});
  }, [isLoggedIn, user.wallet]);

  // Check if username was recently changed
  const recentlyChangedUsername = wasUsernameChangedRecently(user.usernameChangedAt);
  const hasUsernameHistory = user.usernameHistory && user.usernameHistory.length > 0;

  // Split children into chart, trades/portfolio, and optional sidebar extras
  const childrenArray = Children.toArray(children);
  const chartComponent = childrenArray[0];
  const tradesComponent = childrenArray[1];
  const sidebarExtra = childrenArray[2] || null;   // e.g. Related Articles
  const sidebarAbout = childrenArray[3] || null;   // e.g. About + Categories

  useEffect(() => {
    if (!isLoggedIn) {
      // Logged-out users never copy — status is trivially resolved, show the
      // Copy CTA immediately (no skeleton).
      setIsCopyingTrader(false);
      setCopyLeaderId(null);
      setCopyStatusResolved(true);
      return;
    }
    setCopyStatusResolved(false);
    authFetch("/api/copytrading/following")
      .then((res) => res.json())
      .then((data) => {
        const following = data.following || [];
        const match = following.find(
          (f: { polymarket_address: string; leader_id: string }) =>
            f.polymarket_address?.toLowerCase() === user.wallet.toLowerCase()
        );
        if (match) {
          setIsCopyingTrader(true);
          setCopyLeaderId(match.leader_id);
        } else {
          setIsCopyingTrader(false);
          setCopyLeaderId(null);
        }
      })
      .catch((err) => logger.error("Error fetching following", { error: err instanceof Error ? err.message : String(err) }))
      .finally(() => setCopyStatusResolved(true));
  }, [isLoggedIn, user.wallet]);

  useEffect(() => {
    if (!isLoggedIn) {
      setIsInWatchlist(false);
      setWatchlistResolved(true);
      return;
    }
    setWatchlistResolved(false);
    fetch(`/api/watchlist?source=${source}`)
      .then((res) => res.json())
      .then((data) => {
        const wallets = data.wallets || [];
        setIsInWatchlist(wallets.some((w: { address: string }) => w.address.toLowerCase() === user.wallet.toLowerCase()));
      })
      .catch((err) => logger.error("Error fetching watchlist", { error: err instanceof Error ? err.message : String(err) }))
      .finally(() => setWatchlistResolved(true));
  }, [isLoggedIn, user.wallet, source]);

  const handleStopCopying = async () => {
    if (!copyLeaderId) return;
    setIsStoppingCopy(true);
    try {
      const res = await authFetch("/api/copytrading/unfollow", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leaderId: copyLeaderId }),
      });
      if (res.ok) {
        setIsCopyingTrader(false);
        setCopyLeaderId(null);
      }
    } catch (error) {
      logger.error("Error stopping copy", { error: error instanceof Error ? error.message : String(error) });
    } finally {
      setIsStoppingCopy(false);
    }
  };

  const copyWallet = () => {
    navigator.clipboard.writeText(user.wallet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleWatchlist = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isLoggedIn) return;
    setIsInWatchlist(!isInWatchlist);
    if (!isInWatchlist) {
      const rect = e.currentTarget.getBoundingClientRect();
      triggerConfetti({
        particleCount: 80,
        spread: 60,
        origin: { x: (rect.left + rect.width / 2) / window.innerWidth, y: (rect.top + rect.height / 2) / window.innerHeight },
        colors: ["#ffffff", "#e5e7eb", "#d1d5db"],
      });
    }
    try {
      if (isInWatchlist) {
        await fetch(`/api/watchlist/${user.wallet}?source=${source}`, { method: "DELETE" });
      } else {
        await fetch("/api/watchlist", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ address: user.wallet, source }),
        });
      }
    } catch (error) {
      setIsInWatchlist(isInWatchlist);
    }
  };

  const isActive = user.username === 'royalrabbit' || (user.recentTrades && user.recentTrades.length > 0 &&
    new Date(user.recentTrades[0].date).getTime() > Date.now() - 30 * 60 * 1000);

  // Prefer FrenFlow username, fallback to Polymarket username
  const baseUsername = (user.isFrenFlowUser && user.frenFlowUsername) ? user.frenFlowUsername : user.username;
  // Truncate: for wallets (0x...) show first 6 + last 4, otherwise truncate at 14 chars
  const displayUsername = baseUsername.startsWith("0x")
    ? `${baseUsername.slice(0, 6)}...${baseUsername.slice(-4)}`
    : baseUsername.length > 14
      ? `${baseUsername.slice(0, 14)}...`
      : baseUsername;
  const badge = getBalanceBadge(user.currentBalance);

  return (
    <main className="bg-white dark:bg-[#0f0f12] pb-24 lg:pb-0">
      {/* Back Button */}
      <div className="w-full pt-4 md:pt-6 pb-2 md:pb-4">
        <Link
          href={isKalshi ? "/traders/kalshi" : isPredict ? "/traders/predict" : "/traders"}
          className="font-satoshi inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-accent-primary transition-colors text-sm"
        >
          <FiArrowLeft className="w-4 h-4" />
          <span>{t("traders.profile.back")}</span>
        </Link>
      </div>

      {/* MOBILE LAYOUT (< lg) */}
      <section className="lg:hidden pb-4">
        {/* DexTools copytrading landing — above the profile card. Self-gated. */}
        <DextoolsLandingBar fullWidth />
        <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden transition-colors duration-300">
          <div className="p-4 relative">
            {/* Top-right favorite star — pinned to the card corner with
                a small inset so it never collides with the entry-edge %
                badge that sits at the tail of the username row. */}
            {isLoggedIn && watchlistResolved && (
              <button
                onClick={toggleWatchlist}
                onMouseEnter={() => setWatchlistHovered(true)}
                onMouseLeave={() => setWatchlistHovered(false)}
                className={`absolute top-2 right-2 z-10 p-1 rounded-[5px] transition-colors ${
                  isInWatchlist
                    ? 'text-yellow-400 hover:bg-yellow-400/10'
                    : 'text-gray-400 hover:text-yellow-400 hover:bg-white/[0.06]'
                }`}
                aria-label={isInWatchlist ? t("traders.tip.favRemove") : t("traders.tip.favAdd")}
                title={isInWatchlist ? t("traders.tip.favRemove") : t("traders.tip.favAdd")}
              >
                <Star size={18} className={isInWatchlist ? 'fill-current' : ''} />
              </button>
            )}
            {/* Row 1: Avatar + Username + Badge */}
            <div className="flex items-center gap-3 mb-3">
              <div className="relative w-14 h-14 flex-shrink-0">
                {(user.frenFlowAvatar || user.avatar) ? (
                  <CdnImage
                    src={user.frenFlowAvatar || user.avatar}
                    alt={user.username}
                    width={56}
                    height={56}
                    className={`w-full h-full object-cover rounded-[5px] border-2 ${user.isFrenFlowUser ? 'border-white/40' : 'border-gray-300 dark:border-white/15'}`}
                  />
                ) : (
                  <GradientAvatar seed={user.wallet} size={56} className={`border-2 ${user.isFrenFlowUser ? 'border-white/40' : 'border-gray-300 dark:border-white/15'}`} />
                )}
                {user.verified && (
                  <div className={`absolute -bottom-1 ${user.isFrenFlowUser ? '-left-1' : '-right-1'} bg-accent-primary rounded-[5px] p-1 border-2 border-white dark:border-[#0f0f12]`}>
                    <FiShield className="w-2.5 h-2.5 text-white dark:text-[#0A0A0A]" />
                  </div>
                )}
                {/* Logo FrenFlow como overlay del avatar (sustituye al badge
                    que iba al lado del nombre). Marca de "es usuario FrenFlow". */}
                {user.isFrenFlowUser && (
                  <div
                    className="absolute -bottom-1 -right-1 bg-white dark:bg-[#18181D] rounded-[5px] p-1 border border-gray-200 dark:border-[#1A1A1A] shadow-sm"
                    title="FrenFlow User"
                  >
                    <img src="/brand/frenflow_logo.png" alt="FrenFlow" className="w-4 h-4 rounded-sm" />
                  </div>
                )}
                {/* X handle overlay — bottom-right of the avatar, matching the
                    /traders grid card (2026-06-10, white chip / black X). Only
                    when that corner is free (no FrenFlow logo / verified shield)
                    so it never overlaps. The full X button still lives below. */}
                {user.xUsername && !user.isFrenFlowUser && !user.verified && (
                  <a
                    href={`https://x.com/${user.xUsername}`}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    onClick={(e) => e.stopPropagation()}
                    className="absolute -bottom-1 -right-1 inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-white text-black border-2 border-white dark:border-[#0f0f12] hover:opacity-80 transition-opacity"
                    title={t("traders.profile.xLink", { handle: user.xUsername || "" })}
                    aria-label={t("traders.profile.xLink", { handle: user.xUsername || "" })}
                  >
                    <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </a>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap pr-8">
                  <h1 className="font-monument text-lg font-bold text-gray-900 dark:text-white truncate">
                    {displayUsername}
                  </h1>
                  {isLoggedIn && !isKalshi && user.wallet && (
                    <TraderLabelEditor
                      address={user.wallet}
                      username={baseUsername}
                      customLabel={myTraderLabel}
                      mode="chip"
                      onSaved={setMyTraderLabel}
                    />
                  )}
                  {/* HFT bot badge — back on the username row per user
                      feedback. Whale balance emoji is the only badge
                      that moves to the wallet line (it reads as a
                      portfolio-size indicator, not an identity). */}
                  {isHft && (
                    <span
                      className="px-1.5 py-0.5 rounded-[5px] text-[9px] font-bold bg-red-500/15 text-red-500 border border-red-500/30"
                      title={hftTradesPerHour ? `${hftTradesPerHour} trades/hour` : undefined}
                    >
                      HFT BOT
                    </span>
                  )}
                  {/* Style chip + secondary styles + entry-edge removed
                      2026-06-15 to match the grid/carousel (low-signal noise);
                      HFT warning stays. */}
                  {isActive && (
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF9D] opacity-75"></span>
                      <span className="relative inline-flex rounded-[5px] h-2 w-2 bg-[#00FF9D]"></span>
                    </span>
                  )}
                  {/* Username change indicator */}
                  {(recentlyChangedUsername || hasUsernameHistory) && (
                    <button
                      onClick={() => setShowUsernameHistory(!showUsernameHistory)}
                      className="flex items-center gap-1 px-1.5 py-0.5 bg-yellow-500/10 border border-yellow-500/30 rounded-[5px] text-[10px] text-yellow-500 hover:bg-yellow-500/20 transition-colors"
                      title={t("traders.profile.aka.tooltip")}
                    >
                      <span>{t("traders.profile.aka")}</span>
                    </button>
                  )}
                </div>
                {/* Username history dropdown */}
                {showUsernameHistory && hasUsernameHistory && (
                  <div className="mt-1 p-2 bg-gray-100 dark:bg-[#0a0a0b] rounded-[5px] text-[11px]">
                    <div className="text-gray-500 dark:text-gray-400 mb-1">{t("traders.profile.previously")}</div>
                    {user.usernameHistory!.slice(0, 5).map((entry, i) => (
                      <div key={i} className="flex items-center justify-between py-0.5">
                        <span className="text-gray-700 dark:text-gray-300">@{entry.old_username}</span>
                        <span className="text-gray-400 text-[10px]">{formatUsernameChangeTime(entry.changed_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Pseudonym - Mobile */}
                {user.pseudonym && user.pseudonym !== user.username && (
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">aka {user.pseudonym}</p>
                )}
                {/* Wallet line — directly under the name (2026-06-15, moved
                    out of the full-width row below the avatar so it reads as
                    part of the identity block). Balance tier as a text badge
                    (no emoji) + address + copy + explorer link. */}
                {user.wallet && user.wallet.startsWith('0x') && (
                  <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                    {badge && (
                      <span className="px-1.5 py-0.5 rounded-[5px] text-[9px] font-semibold uppercase tracking-wide bg-gray-100 dark:bg-white/[0.06] text-gray-500 dark:text-gray-400">
                        {badge.label}
                      </span>
                    )}
                    <code className="font-mono text-[11px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-[#0a0a0b] px-1.5 py-0.5 rounded">
                      {user.wallet.slice(0, 6)}...{user.wallet.slice(-4)}
                    </code>
                    <button onClick={copyWallet} className="p-1 hover:bg-gray-100 dark:hover:bg-[#0a0a0b] rounded">
                      {copied ? <FiCheck className="w-3 h-3 text-[#00FF9D]" /> : <FiCopy className="w-3 h-3 text-gray-400" />}
                    </button>
                    <a href={isPredict ? `https://bscscan.com/address/${user.wallet}` : `https://polygonscan.com/address/${user.wallet}`} target="_blank" rel="noopener noreferrer nofollow" className="p-1 hover:bg-gray-100 dark:hover:bg-[#0a0a0b] rounded" title={isPredict ? t("traders.profile.viewBscScan") : t("traders.profile.viewPolygonscan")}>
                      {isPredict ? (
                        <img src="/bnb-logo.svg" alt="BscScan" width={12} height={12} className="w-3 h-3 opacity-50 hover:opacity-100" />
                      ) : (
                        <CdnImage src="/polygonscan.png" alt="Polygonscan" width={12} height={12} className="w-3 h-3 opacity-50 hover:opacity-100" />
                      )}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Row 2: Performance — PM leads with a PnL hero (the metric that
                actually drives the copy decision); ROI · win · trades ride the
                subline and the full breakdown lives in the stats panel below.
                Kalshi/Predict keep their native metric row. 2026-06-15:
                replaced the flat 4-stat row that read as dead space when
                balance/biggest-win came back zero. */}
            {!isKalshi && !isPredict ? (
              <div className="text-center bg-gray-50 dark:bg-[#18181D]/20 rounded-[5px] px-3 py-3 mb-3">
                <div
                  title={t("traders.profile.netPnl")}
                  className={`font-sans text-3xl font-black tracking-tight ${(user.totalPnL || 0) >= 0 ? 'text-[#00FF9D]' : 'text-[#FF4444]'}`}
                >
                  {(user.totalPnL || 0) >= 0 ? '+' : ''}{formatCurrency(user.totalPnL)}
                </div>
                <div className="font-satoshi text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                  {typeof user.profitPercent === 'number' && Number.isFinite(user.profitPercent) && (
                    <>
                      <span className={`font-semibold ${user.profitPercent >= 0 ? 'text-[#00FF9D]/80' : 'text-[#FF4444]/80'}`}>
                        {user.profitPercent >= 0 ? '+' : ''}{user.profitPercent.toFixed(1)}% {t("traders.psp.row.roi") || "ROI"}
                      </span>
                      {' · '}
                    </>
                  )}
                  {user.winRate > 0 ? `${user.winRate}% ${t("traders.profile.stat.winRate")} · ` : ''}
                  {(user.totalTrades || 0).toLocaleString()} {t("traders.profile.stat.predictions")}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-gray-50 dark:bg-[#18181D]/20 rounded-[5px] px-3 py-2.5 mb-3">
                <div className="text-center">
                  <div className="font-monument text-sm font-bold text-gray-900 dark:text-white">
                    {isKalshi ? formatCurrency(Number(user.totalVolume) || 0) : (((user as any).totalPoints || 0) >= 1000 ? `${(((user as any).totalPoints || 0) / 1000).toFixed(0)}K` : ((user as any).totalPoints || 0).toFixed(0))}
                  </div>
                  <div className="font-satoshi text-[10px] text-gray-500 dark:text-gray-400">
                    {isKalshi ? t("traders.profile.stat.volume") : t("traders.profile.stat.points")}
                  </div>
                </div>
                <div className="w-px h-8 bg-gray-200 dark:bg-[#18181D]/50"></div>
                <div className="text-center">
                  <div className="font-monument text-sm font-bold text-[#00FF9D]">
                    {isKalshi ? (kalshiData.followers || 0) : `#${(user as any).rank || '—'}`}
                  </div>
                  <div className="font-satoshi text-[10px] text-gray-500 dark:text-gray-400">
                    {isKalshi ? t("traders.profile.stat.followers") : t("traders.profile.stat.rank")}
                  </div>
                </div>
                <div className="w-px h-8 bg-gray-200 dark:bg-[#18181D]/50"></div>
                <div className="text-center">
                  <div className="font-monument text-sm font-bold text-white dark:text-white">{user.totalTrades}</div>
                  <div className="font-satoshi text-[10px] text-gray-500 dark:text-gray-400">
                    {isKalshi ? t("traders.profile.stat.markets") : t("traders.profile.stat.predictions")}
                  </div>
                </div>
                {isPredict && typeof user.profitPercent === 'number' && Number.isFinite(user.profitPercent) && (
                  <>
                    <div className="w-px h-8 bg-gray-200 dark:bg-[#18181D]/50"></div>
                    <div className="text-center">
                      <div className={`font-monument text-sm font-bold ${user.profitPercent >= 0 ? 'text-[#00FF9D]' : 'text-[#FF4444]'}`}>
                        {user.profitPercent >= 0 ? '+' : ''}{user.profitPercent.toFixed(1)}%
                      </div>
                      <div className="font-satoshi text-[10px] text-gray-500 dark:text-gray-400">
                        {t("traders.psp.row.roi") || "ROI"}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Copy Button - PROMINENT CTA */}
            <>
                {isSelfTrader ? null : (isLoggedIn && !copyStatusResolved) ? (
                  // Until the following-status fetch settles, show a neutral
                  // skeleton instead of the lime "Copy" CTA — otherwise an
                  // already-copied trader flashes "Copy"→"Copying" on entry.
                  <div className="w-full h-[56px] mb-3 rounded-[5px] bg-gray-100 dark:bg-[#18181D]/60 border border-gray-200 dark:border-[#1A1A1A] animate-pulse" aria-hidden />
                ) : isCopyingTrader ? (
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={handleStopCopying}
                      disabled={isStoppingCopy}
                      className="font-inter-tight flex-1 py-3.5 bg-[#00FF9D]/10 border border-[#00FF9D]/30 text-[#00FF9D] font-bold text-base rounded-[5px] flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isStoppingCopy ? (
                        <div className="w-4 h-4 border-2 border-[#00FF9D] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <FiCheck className="w-5 h-5" />
                      )}
                      {isStoppingCopy ? t("traders.profile.stopping") : t("traders.profile.copying")}
                    </button>
                    <button
                      onClick={() => handleCopyClick()}
                      className="py-3.5 px-4 bg-gray-100 dark:bg-[#18181D]/50 border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] hover:bg-gray-200 dark:hover:bg-[#18181D] transition-colors"
                    >
                      <span className="text-lg">⚙️</span>
                    </button>
                  </div>
                ) : (
                  // 2026-06-18: de-limed — white-solid CTA (bg-gray-900
                  // dark:bg-white) to match the Copy CTA across /traders
                  // grid + table. Single "Copy this trader" visual grammar,
                  // no lime (per CLAUDE.md: neon/yellow CTAs prohibited).
                  // BoltIcon (local SVG) replaces the cross-fingers
                  // emoji; matches the lightning glyph used elsewhere.
                  <button
                    onClick={() => handleCopyClick()}
                    className="font-inter-tight w-full py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-extrabold text-base rounded-[5px] flex items-center justify-center gap-2 hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors mb-3 active:scale-[0.98]"
                  >
                    <BoltIcon className="w-5 h-5" />
                    {t("traders.profile.copyCta")}
                  </button>
                )}
            </>

            {/* Admin-only: who does this trader copy (gated to Mirror allowlist) */}
            {!isKalshi && !isPredict && <AdminCopiesButton wallet={user.wallet} />}

            {/* Row 5: Secondary Actions */}
            <div className="flex gap-2 mb-2">
              {user.isFrenFlowUser && user.frenFlowUsername && (
                <a
                  href={`/profile/${user.frenFlowUsername}`}
                  className="font-inter-tight flex-1 py-2.5 bg-white/10 border border-white/20 text-white font-semibold text-sm rounded-[5px] flex items-center justify-center gap-2"
                >
                  <img src="/brand/frenflow_logo.png" alt="" className="w-4 h-4" />
                  {t("traders.profile.tab.profile")}
                </a>
              )}
              <a
                href={isKalshi ? `https://kalshi.com/ideas/profiles/${user.username}` : isPredict ? `https://predict.fun/portfolio/${user.wallet}` : `https://polymarket.com/profile/${user.wallet}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-inter-tight flex-1 py-2.5 bg-gray-100 dark:bg-[#18181D]/50 border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300 font-semibold text-sm rounded-[5px] flex items-center justify-center gap-2"
              >
                {isKalshi ? (
                  <CdnImage src="/kalshi-icon.png" alt="" width={16} height={16} className="w-4 h-4" />
                ) : isPredict ? (
                  <CdnImage src="/logos/predictfun/Predict-Icon-BP.png" alt="" width={16} height={16} className="w-4 h-4" />
                ) : (
                  <CdnImage src="/polymarket-logo-blue.png" alt="" width={16} height={16} className="w-4 h-4" />
                )}
                {isKalshi ? t("traders.profile.viewOnKalshi") : isPredict ? t("traders.profile.viewOnPredict") : t("traders.profile.viewOnPolymarket")}
              </a>
              {/* Standalone X button removed 2026-06-10 — the X link now lives
                  as the overlay chip on the avatar. */}
              {/* Watchlist toggle moved to top-right star button. */}
            </div>

            {/* Row 6: Copiers/Followers count */}
            <p className="font-satoshi text-center text-xs text-gray-500 dark:text-gray-400">
              {isKalshi ? (
                <><span className="font-semibold text-gray-900 dark:text-white">{kalshiData.followers || 0}</span> followers · <span className="font-semibold text-gray-300">{kalshiData.following || 0}</span> following</>
              ) : (user.copiers || 0) > 0 ? (
                <><span className="font-semibold text-gray-900 dark:text-white">{user.copiers}</span> {user.copiers === 1 ? t("traders.profile.copiers.one") : t("traders.profile.copiers.many")} {t("traders.profile.copiers.suffix")}</>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">{t("traders.profile.copiers.first")}</span>
              )}
            </p>
          </div>
        </div>

        {/* Joined & Views - Outside the card */}
        {(user.joinedDate && user.joinedDate !== "Unknown") || (user.profileViews !== undefined && user.profileViews > 0) ? (
          <div className="flex items-center justify-center gap-3 mt-3 text-[11px] text-gray-400">
            {user.joinedDate && user.joinedDate !== "Unknown" && (
              <span className="flex items-center gap-1">
                <FiCalendar className="w-3 h-3" />
                {t("traders.profile.joined", { date: new Date(user.joinedDate).toLocaleDateString(undefined, { month: "short", year: "numeric" }) })}
              </span>
            )}
            {user.profileViews !== undefined && user.profileViews > 0 && (
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {t("traders.profile.views", { count: user.profileViews })}
              </span>
            )}
          </div>
        ) : null}

        {/* Mobile: Kalshi categories + badges */}
        {isKalshi && kalshiData.categories && kalshiData.categories.length > 0 && (() => {
          const cats = kalshiData.categories.slice(0, 8);
          const totalValue = cats.reduce((sum: number, c: any) => sum + (c.value || 0), 0);
          const maxValue = Math.max(...cats.map((c: any) => c.value || 0));
          const DONUT_COLORS = ['#10b981', '#3b82f6', '#a855f7', '#f59e0b', '#f43f5e', '#06b6d4', '#ec4899', '#14b8a6'];

          const radius = 50;
          const circumference = 2 * Math.PI * radius;
          let cumulativeOffset = 0;
          const segments = cats.map((cat: any, i: number) => {
            const pct = totalValue > 0 ? (cat.value || 0) / totalValue : 0;
            const dashLength = pct * circumference;
            const gap = circumference - dashLength;
            const offset = -cumulativeOffset;
            cumulativeOffset += dashLength;
            return { dashArray: `${dashLength} ${gap}`, offset, color: DONUT_COLORS[i % DONUT_COLORS.length] };
          });

          return (
            <div className="mt-4 space-y-3">
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4">
                <div className="text-sm text-gray-400 font-medium mb-3">{t("traders.profile.volByCategory")}</div>
                {/* Donut centered */}
                <div className="flex justify-center mb-4">
                  <svg width="120" height="120" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r={radius} fill="none" stroke="#1A1A1A" strokeWidth="14" />
                    {segments.map((seg: any, i: number) => (
                      <circle key={i} cx="60" cy="60" r={radius} fill="none" stroke={seg.color} strokeWidth="14"
                        strokeDasharray={seg.dashArray} strokeDashoffset={seg.offset} strokeLinecap="butt"
                        transform="rotate(-90 60 60)" className="opacity-80" />
                    ))}
                    <text x="60" y="57" textAnchor="middle" className="fill-white font-monument text-[10px] font-bold">{formatCurrency(totalValue)}</text>
                    <text x="60" y="69" textAnchor="middle" className="fill-gray-500 text-[8px]">{t("traders.profile.totalVol")}</text>
                  </svg>
                </div>
                {/* Category list */}
                <div className="space-y-2">
                  {cats.map((cat: any, i: number) => {
                    const pct = maxValue > 0 ? ((cat.value || 0) / maxValue) * 100 : 0;
                    const totalPct = totalValue > 0 ? ((cat.value || 0) / totalValue) * 100 : 0;
                    return (
                      <div key={cat.category}>
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-[5px] flex-shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                            <span className="text-xs text-gray-300 capitalize truncate">{cat.category.replace(/_/g, ' ')}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs font-medium text-white">{formatCurrency(cat.value)}</span>
                            <span className="text-[10px] text-gray-500 w-7 text-right">{totalPct.toFixed(0)}%</span>
                          </div>
                        </div>
                        <div className="w-full h-1 bg-white/[0.04] rounded-[5px] overflow-hidden ml-4">
                          <div className="h-full rounded-[5px]" style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] + '99' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Mobile badges */}
              {kalshiData.badges && kalshiData.badges.length > 0 && (
                <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4">
                  <div className="text-sm text-gray-400 font-medium mb-2">{t("traders.profile.badges")}</div>
                  <div className="flex flex-wrap gap-2">
                    {kalshiData.badges.map((badge: any) => (
                      <div key={badge.id} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-[#0a0a0b] rounded-[5px] border border-gray-200 dark:border-[#222]" title={badge.description}>
                        <div className="w-4 h-4 rounded-[5px] flex-shrink-0" style={{ backgroundColor: badge.colorHex || '#333' }} />
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{badge.displayName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Mobile: Predict categories */}
        {isPredict && (user as any).categories && (user as any).categories.length > 0 && (() => {
          const cats = ((user as any).categories as Array<{ name: string; marketsCount: number }>)
            .sort((a, b) => b.marketsCount - a.marketsCount).slice(0, 8);
          const totalMarkets = cats.reduce((sum, c) => sum + c.marketsCount, 0);
          const DONUT_COLORS = ['#10b981', '#3b82f6', '#a855f7', '#f59e0b', '#f43f5e', '#06b6d4', '#ec4899', '#14b8a6'];

          return (
            <div className="mt-4">
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4">
                <div className="text-sm text-gray-400 font-medium mb-3">{t("traders.profile.marketsByCategory")}</div>
                <div className="space-y-2">
                  {cats.map((cat, i) => {
                    const pct = totalMarkets > 0 ? (cat.marketsCount / totalMarkets) * 100 : 0;
                    return (
                      <div key={cat.name} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-[5px]" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                            <span className="text-xs text-gray-300 font-medium truncate max-w-[120px]">{cat.name}</span>
                          </div>
                          <span className="text-xs font-medium text-white">{cat.marketsCount}</span>
                        </div>
                        <div className="w-full h-1 bg-white/[0.04] rounded-[5px] overflow-hidden">
                          <div className="h-full rounded-[5px]" style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] + '99' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}
      </section>

      {/* Predexon Stats Dashboard — mobile only (desktop uses compact in right sidebar).
          Render a skeleton with matching height while loading so the async fetch doesn't
          push the mobile children (chart + trades) down when it resolves. */}
      {predexonProfile?.metrics ? (
        <section className="mb-4 lg:hidden">
          <PredexonStatsPanel profile={predexonProfile} />
        </section>
      ) : predexonLoading ? (
        <section className="mb-4 lg:hidden">
          <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-5 animate-pulse min-h-[400px]">
            <div className="h-3 w-24 bg-gray-200 dark:bg-white/[0.04] rounded mb-3" />
            <div className="h-8 w-40 bg-gray-200 dark:bg-white/[0.04] rounded mb-4" />
            <div className="h-8 w-full bg-gray-200 dark:bg-white/[0.04] rounded mb-3" />
            <div className="space-y-2">
              <div className="h-4 w-full bg-gray-200 dark:bg-white/[0.04] rounded" />
              <div className="h-4 w-full bg-gray-200 dark:bg-white/[0.04] rounded" />
              <div className="h-4 w-full bg-gray-200 dark:bg-white/[0.04] rounded" />
              <div className="h-4 w-full bg-gray-200 dark:bg-white/[0.04] rounded" />
            </div>
          </div>
        </section>
      ) : null}

      {/* DESKTOP LAYOUT (lg+) - Three Column Dashboard */}
      <section className="hidden lg:block w-full pb-12">
        <div className="grid gap-5 grid-cols-12">
          {/* LEFT COLUMN - Profile Identity (col-span-3, sticky) */}
          <div className="col-span-3 space-y-4 self-start sticky top-4">
            {/* DexTools copytrading landing — above the profile box, inside the
                left column so it matches its width. Self-gated. */}
            <DextoolsLandingBar fullWidth />
            {/* Profile Card */}
            <div className="bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden transition-colors duration-300">
              <div className="p-5 relative">
                {/* Top-right favorite star — same affordance as the
                    list view + mobile header star. Pinned to the card
                    corner so the entry-edge % badge on the username row
                    doesn't run into it. */}
                {isLoggedIn && watchlistResolved && (
                  <button
                    onClick={toggleWatchlist}
                    onMouseEnter={() => setWatchlistHovered(true)}
                    onMouseLeave={() => setWatchlistHovered(false)}
                    className={`absolute top-2 right-2 z-10 p-1 rounded-[5px] transition-colors ${
                      isInWatchlist
                        ? 'text-yellow-400 hover:bg-yellow-400/10'
                        : 'text-gray-400 hover:text-yellow-400 hover:bg-white/[0.06]'
                    }`}
                    aria-label={isInWatchlist ? t("traders.tip.favRemove") : t("traders.tip.favAdd")}
                    title={isInWatchlist ? t("traders.tip.favRemove") : t("traders.tip.favAdd")}
                  >
                    <Star size={18} className={isInWatchlist ? 'fill-current' : ''} />
                  </button>
                )}
                {/* Top row: Avatar left + Name/wallet right */}
                <div className="flex gap-4 mb-4">
                  {/* Avatar */}
                  <div className="relative w-16 h-16 shrink-0">
                    {(user.frenFlowAvatar || user.avatar) ? (
                      <CdnImage
                        src={user.frenFlowAvatar || user.avatar}
                        alt={user.username}
                        width={64}
                        height={64}
                        className={`w-full h-full object-cover rounded-[5px] ring-2 ${user.isFrenFlowUser ? 'ring-white/40' : 'ring-white/15'}`}
                      />
                    ) : (
                      <GradientAvatar seed={user.wallet} size={64} className={`ring-2 ${user.isFrenFlowUser ? 'ring-white/40' : 'ring-white/15'}`} />
                    )}
                    {user.verified && (
                      <div className={`absolute -bottom-1 ${user.isFrenFlowUser ? '-left-1' : '-right-1'} bg-white rounded-[5px] p-1 border-2 border-[#18181D]`}>
                        <FiShield className="w-2.5 h-2.5 text-[#0f0f12]" />
                      </div>
                    )}
                    {/* Logo FrenFlow como overlay del avatar (sustituye al badge
                        del nombre). El indicador "live" va arriba-derecha. */}
                    {user.isFrenFlowUser && (
                      <div
                        className="absolute -bottom-1 -right-1 bg-white dark:bg-[#18181D] rounded-[5px] p-1 border border-gray-200 dark:border-[#1A1A1A] shadow-sm"
                        title="FrenFlow User"
                      >
                        <img src="/brand/frenflow_logo.png" alt="FrenFlow" className="w-4 h-4 rounded-sm" />
                      </div>
                    )}
                    {/* X handle overlay — bottom-right, matching the grid card +
                        mobile profile (2026-06-10, white chip / black X). Only
                        when that corner is free (no FrenFlow logo / verified
                        shield) so it never overlaps. */}
                    {user.xUsername && !user.isFrenFlowUser && !user.verified && (
                      <a
                        href={`https://x.com/${user.xUsername}`}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        onClick={(e) => e.stopPropagation()}
                        className="absolute -bottom-1 -right-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white text-black border-2 border-[#18181D] hover:opacity-80 transition-opacity"
                        title={t("traders.profile.xLink", { handle: user.xUsername || "" })}
                        aria-label={t("traders.profile.xLink", { handle: user.xUsername || "" })}
                      >
                        <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      </a>
                    )}
                    {isActive && (
                      <div className="absolute -top-1 -right-1">
                        <span className="relative flex h-3.5 w-3.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF9D] opacity-75"></span>
                          <span className="relative inline-flex rounded-[5px] h-3.5 w-3.5 bg-[#00FF9D] border-2 border-[#18181D]"></span>
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Name + wallet + badges */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1 pr-8">
                      <h1 className="font-monument text-lg font-bold text-white truncate">
                        {displayUsername}
                      </h1>
                      {isLoggedIn && !isKalshi && user.wallet && (
                        <TraderLabelEditor
                          address={user.wallet}
                          username={baseUsername}
                          customLabel={myTraderLabel}
                          mode="chip"
                          onSaved={setMyTraderLabel}
                        />
                      )}
                      {/* HFT bot badge — back on the username row per user
                          feedback. Whale balance emoji is the only badge
                          that moves to the wallet line below. */}
                      {isHft && (
                        <span
                          className="px-1.5 py-0.5 rounded-[5px] text-[9px] font-bold bg-red-500/15 text-red-500 border border-red-500/30 shrink-0"
                          title={hftTradesPerHour ? `${hftTradesPerHour} trades/hour` : undefined}
                        >
                          HFT BOT
                        </span>
                      )}
                      {/* Style chip + secondary styles + entry-edge removed
                          2026-06-15 to match the grid/carousel; HFT stays. */}
                      {(recentlyChangedUsername || hasUsernameHistory) && (
                        <button
                          onClick={() => setShowUsernameHistory(!showUsernameHistory)}
                          className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10 border border-yellow-500/30 rounded-[5px] text-[10px] text-yellow-500 hover:bg-yellow-500/20 transition-colors shrink-0"
                          title={t("traders.profile.aka.tooltip")}
                        >
                          <span>{t("traders.profile.aka")}</span>
                        </button>
                      )}
                    </div>

                    {/* Pseudonym */}
                    {user.pseudonym && user.pseudonym !== user.username && (
                      <p className="text-xs text-gray-500 mb-1">aka {user.pseudonym}</p>
                    )}

                    {/* Wallet line — directly under the name (2026-06-15, moved
                        out of the full-width row below the avatar so it reads as
                        part of the identity block). Balance tier badge + address
                        + copy + explorer link; Kalshi shows followers/following. */}
                    {user.wallet && user.wallet.startsWith('0x') ? (
                      <div className="flex items-center flex-wrap gap-2 mt-1">
                        {badge && (
                          <span className="px-1.5 py-0.5 rounded-[5px] text-[9px] font-semibold uppercase tracking-wide bg-gray-100 dark:bg-white/[0.06] text-gray-500 dark:text-gray-400">
                            {badge.label}
                          </span>
                        )}
                        <code className="font-mono text-xs text-gray-500 bg-[#0a0a0b] px-2 py-0.5 rounded">
                          {user.wallet.slice(0, 6)}...{user.wallet.slice(-4)}
                        </code>
                        <button onClick={copyWallet} className="p-1 hover:bg-[#1A1A1A] rounded transition-colors">
                          {copied ? <FiCheck className="w-3 h-3 text-[#00FF9D]" /> : <FiCopy className="w-3 h-3 text-gray-500" />}
                        </button>
                        <a href={isPredict ? `https://bscscan.com/address/${user.wallet}` : `https://polygonscan.com/address/${user.wallet}`} target="_blank" rel="noopener noreferrer nofollow" className="p-1 hover:bg-[#1A1A1A] rounded transition-colors" title={isPredict ? t("traders.profile.viewBscScan") : t("traders.profile.viewPolygonscan")}>
                          {isPredict ? (
                            <img src="/bnb-logo.svg" alt="BscScan" width={12} height={12} className="w-3 h-3 opacity-50 hover:opacity-100" />
                          ) : (
                            <CdnImage src="/polygonscan.png" alt="Polygonscan" width={12} height={12} className="w-3 h-3 opacity-50 hover:opacity-100" />
                          )}
                        </a>
                      </div>
                    ) : isKalshi ? (
                      <p className="text-xs text-gray-500 mt-1">
                        <span className="font-semibold text-white">{kalshiData.followers || 0}</span> followers · <span className="font-semibold text-gray-300">{kalshiData.following || 0}</span> following
                      </p>
                    ) : null}
                  </div>
                </div>

                {/* Username history dropdown */}
                {showUsernameHistory && hasUsernameHistory && (
                  <div className="mb-3 p-2 bg-[#0a0a0b] rounded-[5px] text-xs border border-[#222]">
                    <div className="text-gray-500 mb-1.5 text-[10px] uppercase tracking-wider">{t("traders.profile.previously")}</div>
                    {user.usernameHistory!.slice(0, 5).map((entry, i) => (
                      <div key={i} className="flex items-center justify-between py-0.5">
                        <span className="text-gray-300">@{entry.old_username}</span>
                        <span className="text-gray-500 text-[10px]">{formatUsernameChangeTime(entry.changed_at)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Bio removed from the profile card 2026-06-10 (the
                    auto-generated About section covers identity context). */}

                {/* Performance hero — PnL (PM) lives in the sticky left card so
                    the decision metric stays visible while scrolling; ROI · win ·
                    trades ride the subline. Mirrors the mobile card + grid. The
                    full breakdown stays in the right-column stats panel.
                    2026-06-15: fills the dead space between wallet and CTA. */}
                {!isKalshi && !isPredict && (
                  <div className="text-center bg-white/[0.03] rounded-[5px] px-3 py-3 mb-3">
                    <div
                      title={t("traders.profile.netPnl")}
                      className={`font-sans text-2xl font-black tracking-tight ${(user.totalPnL || 0) >= 0 ? 'text-[#00FF9D]' : 'text-[#FF4444]'}`}
                    >
                      {(user.totalPnL || 0) >= 0 ? '+' : ''}{formatCurrency(user.totalPnL)}
                    </div>
                    <div className="font-satoshi text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      {typeof user.profitPercent === 'number' && Number.isFinite(user.profitPercent) && (
                        <>
                          <span className={`font-semibold ${user.profitPercent >= 0 ? 'text-[#00FF9D]/80' : 'text-[#FF4444]/80'}`}>
                            {user.profitPercent >= 0 ? '+' : ''}{user.profitPercent.toFixed(1)}% {t("traders.psp.row.roi") || "ROI"}
                          </span>
                          {' · '}
                        </>
                      )}
                      {user.winRate > 0 ? `${user.winRate}% ${t("traders.profile.stat.winRate")} · ` : ''}
                      {(user.totalTrades || 0).toLocaleString()} {t("traders.profile.stat.predictions")}
                    </div>
                  </div>
                )}

                {/* CTA Buttons */}
                {(isLoggedIn && !copyStatusResolved) ? (
                  <div className="w-full h-[48px] mb-3 rounded-[5px] bg-gray-100 dark:bg-[#18181D]/60 border border-gray-200 dark:border-[#1A1A1A] animate-pulse" aria-hidden />
                ) : isCopyingTrader ? (
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={handleStopCopying}
                      disabled={isStoppingCopy}
                      className="font-inter-tight flex-1 py-3 bg-[#00FF9D]/10 border border-[#00FF9D]/30 text-[#00FF9D] font-bold text-sm rounded-[5px] flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isStoppingCopy ? <div className="w-4 h-4 border-2 border-[#00FF9D] border-t-transparent rounded-full animate-spin" /> : <FiCheck className="w-4 h-4" />}
                      {isStoppingCopy ? t("traders.profile.stopping") : t("traders.profile.copying")}
                    </button>
                    <button onClick={() => handleCopyClick()} className="py-3 px-3 bg-[#1A1A1A] border border-[#222] rounded-[5px] hover:border-gray-500">
                      <span>⚙️</span>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleCopyClick()}
                    className="font-inter-tight w-full py-3.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-extrabold text-sm rounded-[5px] flex items-center justify-center gap-2 hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors mb-3"
                  >
                    <BoltIcon className="w-4 h-4" />
                    {t("traders.profile.copyCta")}
                  </button>
                )}

                {/* Admin-only: who does this trader copy (gated to Mirror allowlist) */}
                {!isKalshi && !isPredict && <AdminCopiesButton wallet={user.wallet} />}

                {/* Secondary action buttons */}
                <div className="flex gap-2">
                  {user.isFrenFlowUser && user.frenFlowUsername && (
                    <a
                      href={`/profile/${user.frenFlowUsername}`}
                      className="font-inter-tight flex-1 py-2.5 bg-white/10 border border-white/20 text-white font-medium text-xs rounded-[5px] flex items-center justify-center gap-2 hover:bg-white/15 transition-colors"
                    >
                      <img src="/brand/frenflow_logo.png" alt="" className="w-3.5 h-3.5" />
                      {t("traders.profile.tab.profile")}
                    </a>
                  )}
                  <a
                    href={isKalshi ? `https://kalshi.com/ideas/profiles/${user.username}` : isPredict ? `https://predict.fun/portfolio/${user.wallet}` : `https://polymarket.com/profile/${user.wallet}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-inter-tight flex-1 py-2.5 bg-[#1A1A1A] border border-[#222] text-gray-400 font-medium text-xs rounded-[5px] flex items-center justify-center gap-2 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    {isKalshi ? (
                      <CdnImage src="/kalshi-icon.png" alt="" width={14} height={14} className="w-3.5 h-3.5" />
                    ) : isPredict ? (
                      <CdnImage src="/logos/predictfun/Predict-Icon-BP.png" alt="" width={14} height={14} className="w-3.5 h-3.5" />
                    ) : (
                      <CdnImage src="/polymarket-logo-blue.png" alt="" width={14} height={14} className="w-3.5 h-3.5" />
                    )}
                    {isKalshi ? t("traders.profile.viewOnKalshi") : isPredict ? t("traders.profile.viewOnPredict") : t("traders.profile.viewOnPolymarket")}
                  </a>
                  {/* Standalone X button removed 2026-06-10 — the X link now
                      lives as the overlay chip on the avatar. */}
                  {/* Watchlist toggle moved to top-right star button. */}
                </div>

              </div>
            </div>

            {/* 2026-05-22 (Pika): Copiers/Followers count moved
                inline next to "Joined" / "Views" to read as a
                single social-proof row instead of a centered orphan
                line above the join date. Reads e.g.:
                "📅 Joined Apr 2026 · 👥 0 copying · 👁 12 views". */}
            <div className="flex items-center justify-center gap-4 text-xs text-gray-500 flex-wrap">
              {user.joinedDate && user.joinedDate !== "Unknown" && (
                <span className="font-satoshi flex items-center gap-1.5">
                  <FiCalendar className="w-3 h-3" />
                  {t("traders.profile.joined", { date: new Date(user.joinedDate).toLocaleDateString(undefined, { month: "short", year: "numeric" }) })}
                </span>
              )}
              {isKalshi ? (
                <span className="font-satoshi flex items-center gap-1.5">
                  <FiUsers className="w-3 h-3" />
                  <span className="font-semibold text-white">{kalshiData.followers || 0}</span> followers
                </span>
              ) : (user.copiers || 0) > 0 ? (
                <span className="font-satoshi flex items-center gap-1.5">
                  <FiUsers className="w-3 h-3" />
                  <span className="font-semibold text-white">{user.copiers}</span> copying
                </span>
              ) : null}
              {user.profileViews !== undefined && user.profileViews > 0 && (
                <span className="font-satoshi flex items-center gap-1.5">
                  <Eye className="w-3 h-3" />
                  {t("traders.profile.views", { count: user.profileViews })}
                </span>
              )}
            </div>

            {/* Sidebar extras (e.g. Related Articles) */}
            {sidebarExtra}

            {/* About + Categories */}
            {sidebarAbout}
          </div>

          {/* CENTER COLUMN - Chart + Positions */}
          <div className="col-span-6 space-y-4">
            {isKalshi ? (
              <>
                {/* DFlow PnL Chart */}
                {chartComponent}

                {/* Donut Chart + Category Breakdown */}
                {kalshiData.categories && kalshiData.categories.length > 0 && (() => {
                  const cats = kalshiData.categories.slice(0, 8);
                  const totalValue = cats.reduce((sum: number, c: any) => sum + (c.value || 0), 0);
                  const maxValue = Math.max(...cats.map((c: any) => c.value || 0));
                  const DONUT_COLORS = ['#10b981', '#3b82f6', '#a855f7', '#f59e0b', '#f43f5e', '#06b6d4', '#ec4899', '#14b8a6'];

                  // SVG donut calculations
                  const radius = 56;
                  const circumference = 2 * Math.PI * radius;
                  let cumulativeOffset = 0;
                  const segments = cats.map((cat: any, i: number) => {
                    const pct = totalValue > 0 ? (cat.value || 0) / totalValue : 0;
                    const dashLength = pct * circumference;
                    const gap = circumference - dashLength;
                    const offset = -cumulativeOffset;
                    cumulativeOffset += dashLength;
                    return { dashArray: `${dashLength} ${gap}`, offset, color: DONUT_COLORS[i % DONUT_COLORS.length] };
                  });

                  return (
                    <div className="bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-5 transition-colors duration-300">
                      <div className="text-sm text-gray-400 font-medium mb-4">{t("traders.profile.volByCategory")}</div>
                      <div className="flex gap-6 items-start">
                        {/* Donut Chart */}
                        <div className="flex-shrink-0">
                          <svg width="140" height="140" viewBox="0 0 140 140">
                            {/* Background circle */}
                            <circle cx="70" cy="70" r={radius} fill="none" stroke="#1A1A1A" strokeWidth="16" />
                            {/* Category segments */}
                            {segments.map((seg: any, i: number) => (
                              <circle
                                key={i}
                                cx="70"
                                cy="70"
                                r={radius}
                                fill="none"
                                stroke={seg.color}
                                strokeWidth="16"
                                strokeDasharray={seg.dashArray}
                                strokeDashoffset={seg.offset}
                                strokeLinecap="butt"
                                transform="rotate(-90 70 70)"
                                className="opacity-80"
                              />
                            ))}
                            {/* Center text */}
                            <text x="70" y="66" textAnchor="middle" className="fill-white font-monument text-[11px] font-bold">{formatCurrency(totalValue)}</text>
                            <text x="70" y="80" textAnchor="middle" className="fill-gray-500 text-[9px]">{t("traders.profile.totalVol")}</text>
                          </svg>
                        </div>

                        {/* Category list with bars */}
                        <div className="flex-1 space-y-2.5 min-w-0">
                          {cats.map((cat: any, i: number) => {
                            const pct = maxValue > 0 ? ((cat.value || 0) / maxValue) * 100 : 0;
                            const totalPct = totalValue > 0 ? ((cat.value || 0) / totalValue) * 100 : 0;
                            return (
                              <div key={cat.category}>
                                <div className="flex items-center justify-between mb-0.5">
                                  <div className="flex items-center gap-2">
                                    <div className="w-2.5 h-2.5 rounded-[5px] flex-shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                                    <span className="text-xs text-gray-300 capitalize truncate">{cat.category.replace(/_/g, ' ')}</span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className="text-xs font-medium text-white">{formatCurrency(cat.value)}</span>
                                    <span className="text-[10px] text-gray-500 w-8 text-right">{totalPct.toFixed(0)}%</span>
                                  </div>
                                </div>
                                <div className="w-full h-1 bg-white/[0.04] rounded-[5px] overflow-hidden ml-4">
                                  <div
                                    className="h-full rounded-[5px]"
                                    style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] + '99' }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Badges */}
                {kalshiData.badges && kalshiData.badges.length > 0 && (
                  <div className="bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-5 transition-colors duration-300">
                    <div className="text-sm text-gray-400 font-medium mb-3">{t("traders.profile.badges")}</div>
                    <div className="flex flex-wrap gap-2">
                      {kalshiData.badges.map((badge: any) => (
                        <div
                          key={badge.id}
                          className="flex items-center gap-2 px-3 py-2 bg-[#0a0a0b] rounded-[5px] border border-[#222]"
                          title={badge.description}
                        >
                          <div
                            className="w-5 h-5 rounded-[5px] flex-shrink-0"
                            style={{ backgroundColor: badge.colorHex || '#333' }}
                          />
                          <span className="text-xs font-medium text-gray-300">{badge.displayName}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : isPredict ? (
              <>
                {/* PnL Chart + Categories side by side — no extra padding */}
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-8">{chartComponent}</div>
                  <div className="col-span-4">
                    {(user as any).categories && (user as any).categories.length > 0 && (() => {
                      const allCats = ((user as any).categories as Array<{ name: string; marketsCount: number }>)
                        .sort((a: any, b: any) => b.marketsCount - a.marketsCount);
                      const topCats = allCats.slice(0, 4);
                      const totalMarkets = allCats.reduce((sum: number, c: any) => sum + c.marketsCount, 0);
                      const DONUT_COLORS = ['#10b981', '#3b82f6', '#a855f7', '#f59e0b', '#f43f5e', '#06b6d4', '#ec4899', '#14b8a6', '#84cc16', '#f97316'];

                      const radius = 40;
                      const circumference = 2 * Math.PI * radius;
                      let cumulativeOffset = 0;
                      const segments = allCats.slice(0, 8).map((cat: any, i: number) => {
                        const pct = totalMarkets > 0 ? cat.marketsCount / totalMarkets : 0;
                        const dashLength = pct * circumference;
                        const gap = circumference - dashLength;
                        const offset = -cumulativeOffset;
                        cumulativeOffset += dashLength;
                        return { dashArray: `${dashLength} ${gap}`, offset, color: DONUT_COLORS[i % DONUT_COLORS.length] };
                      });

                      return (
                        <div className="bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4 h-full flex flex-col">
                          <div className="text-sm text-gray-400 font-medium mb-3">{t("traders.profile.predictionsCount", { count: allCats.length })}</div>
                          <div className="flex justify-center mb-3">
                            <svg width="110" height="110" viewBox="0 0 110 110">
                              <circle cx="55" cy="55" r={radius} fill="none" stroke="#1A1A1A" strokeWidth="14" />
                              {segments.map((seg: any, i: number) => (
                                <circle key={i} cx="55" cy="55" r={radius} fill="none" stroke={seg.color} strokeWidth="14"
                                  strokeDasharray={seg.dashArray} strokeDashoffset={seg.offset} strokeLinecap="butt"
                                  transform="rotate(-90 55 55)" className="opacity-80" />
                              ))}
                              <text x="55" y="51" textAnchor="middle" className="fill-gray-500 text-[8px]">{t("traders.profile.totalDonut")}</text>
                              <text x="55" y="64" textAnchor="middle" className="fill-white font-monument text-[12px] font-bold">{totalMarkets.toLocaleString()}</text>
                            </svg>
                          </div>
                          <div className="space-y-2 flex-1 overflow-y-auto" style={{ maxHeight: showAllCategories ? '300px' : 'none' }}>
                            {(showAllCategories ? allCats : topCats).map((cat: any, i: number) => (
                              <div key={cat.name} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-[5px] flex-shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                                  <span className="text-xs text-gray-300 truncate">{cat.name}</span>
                                </div>
                                <span className="text-xs font-semibold text-white">{cat.marketsCount}</span>
                              </div>
                            ))}
                          </div>
                          {allCats.length > 4 && (
                            <button
                              onClick={() => setShowAllCategories(!showAllCategories)}
                              className="mt-2 text-[11px] text-gray-500 hover:text-gray-300 transition-colors text-center"
                            >
                              {showAllCategories ? t("traders.profile.showLess") : t("traders.profile.viewAll", { count: allCats.length })}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Positions */}
                {tradesComponent}

                {/* Predict Badges */}
                {(user as any).badges && (user as any).badges.length > 0 && (
                  <div className="bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4">
                    <div className="text-sm text-gray-400 font-medium mb-2">{t("traders.profile.badges")}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {((user as any).badges as any[]).map((badge: any, i: number) => (
                        <div key={badge.code || i} className="px-2.5 py-1 bg-[#0a0a0b] rounded-[5px] border border-[#222]">
                          <span className="text-[11px] font-medium text-gray-300">{badge.code?.replace(/_/g, ' ') || 'Badge'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* PnL Chart */}
                {chartComponent}

                {/* Recent Trades */}
                {tradesComponent}
              </>
            )}
          </div>

          {/* RIGHT COLUMN - Stats + PnL Breakdown (col-span-3, sticky) */}
          <div className="col-span-3 space-y-4 self-start sticky top-4">
            {/* Predexon Stats Panel (compact/vertical) — or fallback stats */}
            {predexonProfile?.metrics ? (
              <PredexonStatsPanel profile={predexonProfile} compact />
            ) : (
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
                <div className="px-4 pt-4 pb-3">
                  {/* 2026-05-22 (Pika): re-labelled "PERFORMANCE" to
                      "Net PnL" so the user understands the
                      relationship between this number and the
                      "$8.81M All-Time" headline in the middle column.
                      Verified math on @swisstony: Realized ($5.42M) +
                      Unrealized ($3.03M) − Net Fees ($0.54M) ≈
                      $7.91M, which is what this row shows. The
                      $8.81M next door is the gross All-Time number
                      before fees, so they're related but not equal.
                      The new label removes the ambiguity. */}
                  <div className="text-[10px] font-medium uppercase tracking-wider text-gray-500 mb-1" title="Realized + Unrealized − Net Fees">{t("traders.profile.netPnl")}</div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className={`font-monument text-2xl font-black tracking-tight ${(user.totalPnL || 0) >= 0 ? 'text-[#00FF9D]' : 'text-[#FF4444]'}`}>
                      {(user.totalPnL || 0) >= 0 ? '+' : ''}{formatCurrency(user.totalPnL)}
                    </span>
                    {typeof user.profitPercent === 'number' && Number.isFinite(user.profitPercent) && (
                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-[5px] ${
                          user.profitPercent >= 0
                            ? 'bg-[#00FF9D]/10 text-[#00FF9D]'
                            : 'bg-[#FF4444]/10 text-[#FF4444]'
                        }`}
                        title={t("traders.psp.row.roi") || "ROI"}
                      >
                        {user.profitPercent >= 0 ? '+' : ''}{user.profitPercent.toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="px-4 pb-3 border-t border-gray-100 dark:border-white/[0.04] space-y-1.5">
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-[11px] text-gray-500">{t("traders.profile.stat.volume")}</span>
                    <span className="font-monument text-[13px] font-bold text-white">{user.totalVolume}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-[11px] text-gray-500">{t("traders.profile.stat.posValue")}</span>
                    <span className="font-monument text-[13px] font-bold text-white">{formatCurrency(user.currentBalance)}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-[11px] text-gray-500">{t("traders.profile.stat.biggestWin")}</span>
                    <span className="font-monument text-[13px] font-bold text-[#00FF9D]">{formatCurrency(user.largestWin)}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-[11px] text-gray-500">{t("traders.profile.stat.predictions")}</span>
                    <span className="font-monument text-[13px] font-bold text-white">{user.totalTrades}</span>
                  </div>
                  {user.winRate > 0 && (
                    <div className="flex items-center justify-between py-1.5">
                      <span className="text-[11px] text-gray-500">{t("traders.profile.stat.winRate")}</span>
                      <span className="font-monument text-[13px] font-bold text-white">{user.winRate}%</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* PnL Breakdown Chart */}
            {!isKalshi && user.wallet && (
              <WalletPnLEnriched wallet={user.wallet} />
            )}
          </div>
        </div>
      </section>

      {/* Mobile children (chart + trades stacked) */}
      <div className="lg:hidden space-y-4">
        {children}
      </div>

      <CopyTraderModal
        isOpen={isCopyModalOpen}
        onClose={() => setIsCopyModalOpen(false)}
        onSuccess={(leaderId) => {
          setIsCopyingTrader(true);
          setCopyLeaderId(leaderId);
        }}
        trader={{ username: user.username, wallet: user.wallet, avatar: user.avatar }}
      />
    </main>
  );
}
